import React, { FC, type JSX } from 'react';

import MainLayout from '~/components/layouts/MainLayout';
import ContentOnly from '~/components/layouts/MainLayout/ContentOnly';
import MainLayoutExtension from '~/components/layouts/MainLayout/indexExtension';
import MainLayoutSimple from '~/components/layouts/MainLayout/indexSimple';
import MainLayoutTools from '~/components/layouts/MainLayout/indexTools';
import SimpleHeaderLogoLayout from '~/components/layouts/SimpleHeaderLogoLayout';
import CheckoutOpeningMiddleware from '~/middlewares/CheckoutOpeningMiddleware';
import LiveNotificationMiddleware from '~/middlewares/LiveNotificationMiddleware';
import OnboardingMiddleware from '~/middlewares/OnboardingMiddleware';
import PromoModalMiddleware from '~/middlewares/PromoModalMiddleware';
import SubModalsMiddleware from '~/middlewares/SubModalsMiddleware';
import HomeScreenRetentionMiddleware from '~/modules/home/middlewares/HomeScreenRetention';
import NavigationLayout from '~/modules/settings/components/NavigationLayout';
import { LiveUserNotificationPlaces } from '~/services/abTests/features';

type LayoutProps = {
  children?: React.ReactNode;
};

export const withLayout =
  <T extends Record<any, any>>(
    Layout: FC<LayoutProps>,
    Component: FC<T>,
  ): FC<T> =>
  ({ ...props }: T): JSX.Element => (
    <Layout>
      <Component {...props} />
    </Layout>
  );

// layouts
const withMainLayout = (component: FC): FC => withLayout(MainLayout, component);
const withMainSimpleLayout = (component: FC): FC =>
  withLayout(MainLayoutSimple, component);
const withMainSimpleLayoutNoSidebar = (component: FC): FC =>
  withLayout(ContentOnly, component);
const withMainToolsLayout = (component: FC): FC =>
  withLayout(MainLayoutTools, component);
const withMainExtensionLayout = (component: FC): FC =>
  withLayout(MainLayoutExtension, component);

const withSettingsNavigationLayout = (component: FC): FC =>
  withLayout(NavigationLayout, component);

const withOnboardingLayout = (component: FC): FC =>
  withLayout(OnboardingMiddleware, component);

const withHomeScreenRetentionLayout = (component: FC): FC =>
  withLayout(HomeScreenRetentionMiddleware, component);

const withPromoModalLayout = (component: FC): FC =>
  withLayout(PromoModalMiddleware, component);

const withSubModalsLayout = (component: FC): FC =>
  withLayout(SubModalsMiddleware, component);

const withSimpleHeaderLogoLayout = (component: FC): FC =>
  withLayout(SimpleHeaderLogoLayout, component);

const withCheckoutOpeningLayout = (component: FC): FC =>
  withLayout(CheckoutOpeningMiddleware, component);

const withLiveNotificationLayout =
  (place: LiveUserNotificationPlaces) =>
  <T extends Record<any, any>>(Component: FC<T>): FC<T> =>
  (props: T) => (
    <LiveNotificationMiddleware place={place}>
      <Component {...props} />
    </LiveNotificationMiddleware>
  );

export {
  withMainLayout,
  withMainSimpleLayout,
  withMainSimpleLayoutNoSidebar,
  withMainToolsLayout,
  withMainExtensionLayout,
  withSettingsNavigationLayout,
  withOnboardingLayout,
  withHomeScreenRetentionLayout,
  withPromoModalLayout,
  withSubModalsLayout,
  withSimpleHeaderLogoLayout,
  withCheckoutOpeningLayout,
  withLiveNotificationLayout,
};
